import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import api from "../lib/api";
import { Loader2, Search, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { cn } from "../lib/utils";

// Modern 3D Avatar URLs - Custom generated 3D cartoon avatars
const AVATAR_IMAGES = {
  general: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/d0410589174401255436c430e9774510e228880af29cf83b812f0a95b7b66a6c.png",
  doctor: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/14c0501dd5c798f5de7ff050d85c2cd8ea849c63bb3284320eadd4eb92e21b8b.png",
  lawyer: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/18cf77d688756e7d868b367786c2e2a75732db96d2b8426d53db28d7ab343b18.png",
  teacher: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/99f49247e93e2cfb87bd75ee2d8daebb39815eed3236b945029dff2384223112.png",
  programmer: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/163c9d537c1865490a075109c0d9664e6836a79a40a40333e52de8f945524dab.png",
  writer: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/9771c8c9d5809c48a5fcc46ef0f5e04c7e18244fffcd48c883b533545a254adc.png",
  translator: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/78f3b451335faf3ef9c041a3801aa8a0f798d2fbb68298ded8270ef354a7a925.png",
  financial: "https://static.prod-images.emergentagent.com/jobs/424dae5b-cccd-4e9d-a997-736e3bcf257a/images/74c38857a362a7a1b928cd7cebea9e0a4f3925bacc0ba46fe46e5eb2efe40482.png"
};

// Translations for assistants page
const pageTranslations = {
  ar: {
    title: "اختر مساعدك الذكي",
    subtitle: "8 مساعدين متخصصين بأحدث تقنيات الذكاء الاصطناعي",
    searchPlaceholder: "ابحث عن مساعد...",
    backToChat: "العودة للمحادثة",
    loading: "جاري تحميل المساعدين...",
    noResults: "لا يوجد مساعدين مطابقين للبحث",
    exampleQuestions: "أمثلة على الأسئلة:",
    startChat: "ابدأ المحادثة",
    cancel: "إلغاء"
  },
  en: {
    title: "Choose Your AI Assistant",
    subtitle: "8 specialized assistants with the latest AI technology",
    searchPlaceholder: "Search for an assistant...",
    backToChat: "Back to Chat",
    loading: "Loading assistants...",
    noResults: "No assistants match your search",
    exampleQuestions: "Example questions:",
    startChat: "Start Chat",
    cancel: "Cancel"
  }
};

// Assistant Card Component
function AssistantCard({ assistant, onSelect, isSelected, language }) {
  const isArabic = language === "ar";
  const name = isArabic ? assistant.name_ar : assistant.name;
  const description = isArabic ? assistant.description_ar : assistant.description;
  const examples = isArabic ? assistant.example_questions : (assistant.example_questions_en || assistant.example_questions);
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(assistant)}
      className={cn(
        "relative group cursor-pointer rounded-3xl p-5 transition-all duration-300",
        "bg-gradient-to-br from-secondary/60 to-secondary/30 backdrop-blur-sm",
        "border-2 hover:shadow-2xl",
        isSelected 
          ? "border-primary shadow-lg shadow-primary/20" 
          : "border-transparent hover:border-primary/30"
      )}
    >
      {/* Glow Effect */}
      <div 
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-25 transition-opacity duration-500"
        style={{ background: `radial-gradient(circle at center, ${assistant.color}, transparent 70%)` }}
      />
      
      {/* 3D Avatar */}
      <div 
        className="w-24 h-24 mx-auto mb-4 rounded-2xl overflow-hidden transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: `${assistant.color}10` }}
      >
        <img 
          src={AVATAR_IMAGES[assistant.id] || AVATAR_IMAGES.general}
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      
      {/* Content */}
      <h3 className="text-lg font-bold text-foreground mb-1 text-center">
        {name}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 text-center line-clamp-2">
        {description}
      </p>
      
      {/* Example Questions */}
      <div className="space-y-1.5">
        {examples?.slice(0, 2).map((q, i) => (
          <div 
            key={i}
            className="text-xs text-muted-foreground/70 bg-background/50 rounded-xl px-3 py-2 truncate text-center"
          >
            "{q}"
          </div>
        ))}
      </div>
      
      {/* Selection Indicator */}
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
          style={{ backgroundColor: assistant.color }}
        >
          <Sparkles className="w-4 h-4 text-white" />
        </motion.div>
      )}
    </motion.div>
  );
}

// Main Assistants Page
export default function AssistantsPage() {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const t = pageTranslations[language] || pageTranslations.ar;
  
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    try {
      const response = await api.get("/assistants");
      setAssistants(response.data.assistants || []);
    } catch (error) {
      console.error("Failed to load assistants:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setShowDetails(true);
  };

  const startChat = () => {
    if (selectedAssistant) {
      navigate(`/chat?assistant=${selectedAssistant.id}`);
    }
  };

  const filteredAssistants = assistants.filter(a => {
    const searchLower = searchQuery.toLowerCase();
    return (
      a.name_ar?.includes(searchQuery) || 
      a.description_ar?.includes(searchQuery) ||
      a.name?.toLowerCase().includes(searchLower) ||
      a.description?.toLowerCase().includes(searchLower)
    );
  });

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background safe-area-page">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background" 
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Header with Safe Area */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 pt-24 md:pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t.title}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t.subtitle}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/chat")}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <BackArrow className="w-4 h-4" />
              <span className="hidden sm:inline">{t.backToChat}</span>
            </motion.button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className={cn(
              "absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground",
              isRTL ? "right-4" : "left-4"
            )} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className={cn(
                "w-full bg-secondary/50 border border-border/50 rounded-xl py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
                isRTL ? "pr-12 pl-4" : "pl-12 pr-4"
              )}
            />
          </div>
        </div>
      </div>

      {/* Assistants Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {filteredAssistants.map((assistant, index) => (
            <motion.div
              key={assistant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <AssistantCard
                assistant={assistant}
                onSelect={handleSelectAssistant}
                isSelected={selectedAssistant?.id === assistant.id}
                language={language}
              />
            </motion.div>
          ))}
        </motion.div>

        {filteredAssistants.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t.noResults}</p>
          </div>
        )}
      </div>

      {/* Selected Assistant Details Modal */}
      <AnimatePresence>
        {showDetails && selectedAssistant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-background rounded-3xl p-6 shadow-2xl border border-border/50 my-auto max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div 
                  className="w-28 h-28 rounded-2xl overflow-hidden shrink-0"
                  style={{ backgroundColor: `${selectedAssistant.color}10` }}
                >
                  <img 
                    src={AVATAR_IMAGES[selectedAssistant.id] || AVATAR_IMAGES.general}
                    alt={language === "ar" ? selectedAssistant.name_ar : selectedAssistant.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground mb-1">
                    {language === "ar" ? selectedAssistant.name_ar : selectedAssistant.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {language === "ar" ? selectedAssistant.description_ar : selectedAssistant.description}
                  </p>
                </div>
              </div>

              {/* Greeting */}
              <div 
                className="rounded-2xl p-4 mb-6"
                style={{ backgroundColor: `${selectedAssistant.color}10` }}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {language === "ar" ? selectedAssistant.greeting : (selectedAssistant.greeting_en || selectedAssistant.greeting)}
                </p>
              </div>

              {/* Example Questions */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  {t.exampleQuestions}
                </h3>
                <div className="space-y-2">
                  {(language === "ar" 
                    ? selectedAssistant.example_questions 
                    : (selectedAssistant.example_questions_en || selectedAssistant.example_questions)
                  )?.map((q, i) => (
                    <div 
                      key={i}
                      className="text-sm bg-secondary/50 rounded-xl px-4 py-2.5 text-foreground"
                    >
                      {q}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDetails(false)}
                  className="flex-1 py-3.5 rounded-xl bg-secondary text-muted-foreground font-medium transition-colors hover:bg-secondary/80"
                >
                  {t.cancel}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={startChat}
                  className="flex-1 py-3.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 shadow-lg"
                  style={{ backgroundColor: selectedAssistant.color }}
                >
                  <Sparkles className="w-4 h-4" />
                  {t.startChat}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
